import { z } from 'zod/v4'
import { permissionsSchema } from '@/lib/schema/v2/permission'
import { rolesSchema } from '@/lib/schema/v2/role'
import { paginationLinksSchema, paginationMetaSchema } from '@/lib/schema/v2/pagination'
import { companyLocationsSchema } from '@/lib/schema/v2/company-location'
import { companySchema } from '@/lib/schema/v2/company'
import { zDayJs } from '@/lib/schema/v2/dayjs'
import { ampApiErrorSchema } from '@/lib/schema/v2/amp-api-response'

export const companyContactSchema = z.object({
    company: z.lazy(() => companySchema.nullish()),
    company_uuid: z.uuid().nullish(),
    created_at: zDayJs.nullish(),
    email: z.string().nullish(),
    email_verified_at: zDayJs.nullish(),
    fax: z.string().nullish(),
    first_name: z.string().nullish(),
    home_phone: z.string().nullish(),
    is_active: z.boolean().nullish(),
    is_default: z.boolean().nullish(),
    is_super_admin: z.boolean().nullish(),
    last_name: z.string().nullish(),
    locations: z.lazy(() => companyLocationsSchema.nullish()),
    login_token: z.string().nullish(),
    mobile_phone: z.string().nullish(),
    name_initial: z.string().nullish(),
    permissions: z.lazy(() => permissionsSchema.nullish()),
    position: z.string().nullish(),
    profile_photo_url: z.url().nullish(),
    roles: rolesSchema.nullish(),
    timezone_uuid: z.string().nullish(),
    updated_at: zDayJs.nullish(),
    username: z.string().nullish(),
    uuid: z.uuid().nullish(),
    work_phone: z.string().nullish(),
    work_phone_extension: z.string().nullish(),
})

export const companyContactsSchema = z.array(companyContactSchema)

export const paginatedCompanyContactsResponseSchema = z.object({
    data: companyContactsSchema.nullish(),
    links: paginationLinksSchema.nullish(),
    meta: paginationMetaSchema.nullish(),
    success: z.boolean(),
    error: ampApiErrorSchema.nullish(),
})

export const retrieveCompanyContactResponseSchema = z.object({
    data: companyContactSchema.nullish(),
    success: z.boolean(),
    error: ampApiErrorSchema.nullish(),
})

export const createCompanyContactResponseSchema = z.object({
    data: companyContactSchema.nullish(),
    success: z.boolean(),
    error: ampApiErrorSchema.nullish(),
})

export const companyContactFormSchema = z.object({
    first_name: z
        .string()
        .min(1, {
            error: "You can't leave this empty.",
        })
        .default(''),
    last_name: z
        .string()
        .min(1, {
            error: "You can't leave this empty.",
        })
        .default(''),
    email: z.email().default(''),
    roles: z
        .array(z.uuid())
        .min(1, {
            error: "You can't leave this empty.",
        })
        .default([]),
    timezone_uuid: z.uuid().nullish(),
    locations: z.array(z.uuid()).nullish(),
    position: z.string().nullish(),
    mobile_phone: z.string().nullish(),
    work_phone: z.string().nullish(),
    work_phone_extension: z.string().nullish(),
    fax: z.string().nullish(),
    home_phone: z.string().nullish(),
    company_uuid: z.uuid().nullish(),
    is_default: z.boolean().default(false),
    send_welcome_email: z.boolean().nullish().default(false),
})

export type CompanyContact = z.infer<typeof companyContactSchema>
export type CompanyContacts = z.infer<typeof companyContactsSchema>
export type PaginatedCompanyContactsResponse = z.infer<
    typeof paginatedCompanyContactsResponseSchema
>
export type RetrieveCompanyContactResponse = z.infer<typeof retrieveCompanyContactResponseSchema>
export type CreateCompanyContactResponse = z.infer<typeof createCompanyContactResponseSchema>
export type CompanyContactForm = z.infer<typeof companyContactFormSchema>
